import React from 'react';
import Popover from './Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag, faClock, faCalendarDay, faCalendar, faPersonChalkboard,
    faBookOpenReader, faLocationDot, faSchool, faChair, faCopy, faBookOpen, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import {
    getDaysOfWeek,
    formatTime,
    formatDateRange,
    createMarkup, formatDateRangeDuration
} from '../utils/formatters';

function CRNColumns({ crn, sections, onlinePop, setDrawerVisible, setSelectedCRN, setInstructorDrawerOpen, setInstructorData }) {
    const item = sections[0];
    const instructorName = item.pfaName;
    const instructorEmail = item.pfaEmail;
    const secondaryInsturctorNames = item?.sfaName?.split(',');
    const secondaryInsturctorEmails = item?.sfaEmail?.split(',');
    const sortedSections = [...sections].sort((a, b) => {
        const aHasTime = !!a.beginTime;
        const bHasTime = !!b.beginTime;


        if (aHasTime && !bHasTime) return -1;
        if (!aHasTime && bHasTime) return 1;
        if (!aHasTime && !bHasTime) return 0;


        const aDate = a.ptrmStartDate;
        const bDate = b.ptrmStartDate;

        if (aDate && bDate) {
            const dateComparison = aDate.localeCompare(bDate);
            if (dateComparison !== 0) {
                return dateComparison;
            }
        } else if (aDate) {
            return -1;
        } else if (bDate) {
            return 1;
        }

        return a.beginTime.localeCompare(b.beginTime);
    });

    const labelClass = "font-normal text-gray-600 text-xs mr-1";
    const valueClass = "text-xs font-medium text-gray-800 capitalize";
    const popoverLinkClass = "text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer hover:cursor-lancer capitalize";

    // Clipboard copy handler with feedback
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert(`Copied: ${text}`);
        });
    };

    return (
        <div key={crn} className="px-4 py-3 border-t border-gray-200 bg-white">
            {/* Header: CRN + Status + Badges */}
            <div className="flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between">
                <div className="flex flex-row items-center space-x-2">
                    <span className="font-bold text-base flex items-center">
                        <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                        CRN:
                        <Popover
                            content={
                                <div className="p-2 text-white text-xs space-y-1">
                                    {[
                                        { label: "Date to Enroll", value: formatDateRange(item.lastDateToEnroll) },
                                        { label: "Last day to add class", value: formatDateRange(item.lastDateToEnroll) },
                                        { label: "Last day to drop with a refund", value: formatDateRange(item.refundDate) },
                                        { label: "Census Date", value: formatDateRange(item.censusDate) },
                                        { label: "Last day to declare grade mode", value: formatDateRange(item.grademodeDate) },
                                        { label: "Last day to drop without a \"W\"", value: formatDateRange(item.dropNoWDate) },
                                        { label: "Last day to drop with a \"W\"", value: formatDateRange(item.dropWDate) },
                                    ].map((d, i) => (
                                        <div key={i} className="flex justify-between items-start">
                                            <span className="mr-2 whitespace-nowrap">{d.label}:</span>
                                            <span className="text-right">{d.value}</span>
                                        </div>
                                    ))}
                                </div>
                            }
                            description="Critical Dates for this Course"
                            triggerClassName={popoverLinkClass}
                        >
                            <span
                                onClick={() => {
                                    setSelectedCRN(item);
                                    setDrawerVisible(true);
                                }}
                                className="ml-2 font-bold text-base text-blue-600 hover:underline cursor-pointer"
                            >
                                {crn}
                            </span>
                        </Popover>
                    </span>
                    <span className={`ml-2 text-xs font-bold px-2 py-1 rounded-full ${item.fwSectOpenInd === 'C' ? 'bg-red-100 text-red-700' :
                        item.fwSectOpenInd === 'O' ? 'bg-green-100 text-green-700' :
                            item.fwSectOpenInd === 'W' ? 'bg-blue-100 text-blue-700' :
                                item.fwSectOpenInd === 'R' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-600'
                        }`}>
                        {item.fwSectOpenInd === 'C' ? 'Closed' :
                            item.fwSectOpenInd === 'O' ? 'Open' :
                                item.fwSectOpenInd === 'W' ? 'Waitlist' :
                                    item.fwSectOpenInd === 'R' ? 'Reserved' :
                                        'Unknown'}
                    </span>
                    {item.saprDesc || item.cohortDesc ? (
                        <Popover
                            content={
                                <div className="text-xs space-y-1">
                                    {item.saprDesc && <div> {item.saprDesc}</div>}
                                    {item.cohortDesc && <div><strong>Cohort:</strong> {item.cohortDesc}</div>}
                                </div>
                            }
                            description="Restricted"
                            triggerClassName={popoverLinkClass}
                        >
                            <span className="ml-2 bg-amber-100 text-amber-800 font-medium px-2 py-1 rounded-full">Restriction</span>
                        </Popover>
                    ) : null}
                </div>
                {/* Badges on desktop: align right */}
                <div className="hidden 2xl:flex flex-wrap items-center gap-1.5 text-xs mt-0">
                    {(item.attrchrts || '').split(',').map(a => a.trim()).filter(Boolean).map(attr => {
                        let badgeStyle = "font-medium px-2 py-1 rounded-full";
                        if (attr === 'UC') return <span key={attr} className={`bg-purple-100 text-purple-800 ${badgeStyle}`}>Transferable UC</span>;
                        if (attr === 'CSU') return <span key={attr} className={`bg-indigo-100 text-indigo-800 ${badgeStyle}`}>Transferable CSU</span>;
                        if (attr === 'ZTC') return <span key={attr} className={`bg-green-100 text-green-800 ${badgeStyle}`}>ZTC</span>;
                        if (attr === 'QR') return <span key={attr} className={`bg-sky-100 text-sky-800 ${badgeStyle}`}>QR</span>;
                        return null;
                    })}
                </div>
            </div>

            {/* Course Summary Row */}
            <div className="flex flex-wrap flex-col md:flex-row md:items-center md:space-x-6 mt-2 mb-2 space-y-1 md:space-y-0 text-sm text-gray-700">
                {/* Instructor */}
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faPersonChalkboard} className="mr-1.5 text-gray-500 w-3.5" />
                    <span className="mr-1">Instructor:</span>
                    {instructorName.toLowerCase() === 'staff' ? (
                        <span className="text-xs font-medium text-gray-800">Staff</span>
                    ) : (
                        <Popover
                            content={
                                <div className="text-xs">
                                    <div><strong>{instructorName}</strong></div>
                                    <div>{instructorEmail}</div>
                                </div>
                            }
                            description="Instructor Info"
                            triggerClassName={popoverLinkClass}
                        >
                            <span
                                onClick={() => {
                                    setInstructorDrawerOpen(true);
                                    setInstructorData({
                                        primary: { name: instructorName, email: instructorEmail },
                                        secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                            name: name.trim(),
                                            email: secondaryInsturctorEmails?.[idx]?.trim()
                                        }))
                                    });
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer capitalize"
                            >
                                <div className="inline-flex items-center space-x-1">
                                    <span>{instructorName}</span>
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        className="text-gray-500 cursor-pointer hover:text-blue-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(instructorEmail);
                                        }}
                                    />
                                </div>
                            </span>
                        </Popover>
                    )}
                    {secondaryInsturctorNames?.length > 0 && (
                        <span
                            onClick={() => {
                                setInstructorDrawerOpen(true);
                                setInstructorData({
                                    primary: { name: instructorName, email: instructorEmail },
                                    secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                        name: name.trim(),
                                        email: secondaryInsturctorEmails?.[idx]?.trim()
                                    }))
                                });
                            }}
                            className="ml-2 text-xs text-blue-600 hover:underline cursor-pointer"
                        >
                            +{secondaryInsturctorNames.length} more
                        </span>
                    )}
                </div>
                {/* Units */}
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faBookOpen} className="mr-1.5 text-gray-500 w-3.5" />
                    Units: {item.creditHrLow}
                </div>
                {/* Campus */}
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faLocationDot} className="mr-1.5 text-gray-500 w-3.5" />
                    <span className={labelClass}>Location:</span>
                    {item.bldgAddress ? (
                        <Popover
                            content={<span className="text-xs">{item.bldgAddress}</span>}
                            description="Campus Address"
                            triggerClassName={popoverLinkClass}
                        >
                            <div className="inline-flex items-center space-x-1">
                                <span>{item.campDesc || 'N/A'}</span>
                                <FontAwesomeIcon
                                    icon={faCopy}
                                    className="text-gray-500 cursor-pointer hover:text-blue-600"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(item.bldgAddress);
                                    }}
                                />
                            </div>
                        </Popover>
                    ) : (
                        <span className={valueClass}>{item.campDesc || 'N/A'}</span>
                    )}
                </div>
                {/* Term */}
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-500 w-3.5" />
                    Term: {item.ptrmDesc}
                </div>

            </div>

            {/* Meeting Times Table */}
            <div className="w-full mt-2">
                <div className="hidden md:block">
                    <table className="w-full border bg-gray-50 rounded-md overflow-hidden">
                        <thead>
                            <tr className="bg-gray-100 text-xs text-gray-600">
                                <th className="py-1 px-2 font-medium text-left">Time</th>
                                <th className="py-1 px-2 font-medium text-left">Day(s)</th>
                                <th className="py-1 px-2 font-medium text-left">Format</th>
                                <th className="py-1 px-2 font-medium text-left">Room</th>
                                <th className="py-1 px-2 font-medium text-left">Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSections.map((sec, i) => (
                                <tr key={i} className="border-t text-sm text-gray-700">
                                    <td className="py-1 px-2">
                                        {sec.beginTime && sec.endTime
                                            ? `${formatTime(sec.beginTime)} - ${formatTime(sec.endTime)}`
                                            /* Displays different meeting time information based on the meeting type */
                                            : ['TBA', 'HA'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                            ? `${sec.weeklyHrs} Hrs/Wk In Addition To Any Scheduled Hours`
                                            : ['WEB', 'BDE'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                                    ? `${sec.weeklyHrs} Hrs/Wk Of Online Work`
                                                    : `${sec.weeklyHrs} Hrs/Wk`}
                                    </td>
                                    <td className="py-1 px-2">
                                        {sec.ssrmeetMtypCode?.trim().toUpperCase() === 'HA'
                                            ? 'TBA'
                                            : getDaysOfWeek(sec) || 'Online/TBA'}
                                    </td>
                                    <td className="py-1 px-2">{sec.schdDesc || 'N/A'}</td>
                                    <td className="py-1 px-2">
                                        {onlinePop.hasOwnProperty(sec.stvbldgDesc) ? (
                                            <Popover
                                                content={<span className="text-xs">{onlinePop[sec.stvbldgDesc]}</span>}
                                                description={sec.stvbldgDesc || 'Online Info'}
                                                triggerClassName={popoverLinkClass}
                                            >
                                                {sec.stvbldgDesc}
                                            </Popover>
                                        ) : (
                                            <>
                                                {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : `- ${sec.roomCode}`}
                                            </>
                                        )}
                                    </td>
                                    <td className="py-1 px-2">{formatDateRangeDuration(sec.ptrmStartDate, sec.ptrmEndDate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Mobile: Show as stacked cards */}
                <div className="md:hidden flex flex-col gap-y-2">
                    {sortedSections.map((sec, i) => (
                        <div key={i} className="flex flex-col gap-y-1.5 p-2 rounded-md border bg-gray-50">
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-500 w-3.5" />
                                Time: {sec.beginTime && sec.endTime
                                    ? `${formatTime(sec.beginTime)} - ${formatTime(sec.endTime)}`
                                    : `${sec.weeklyHrs} Hrs/Wk of online work`}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendarDay} className="mr-1.5 text-gray-500 w-3.5" />
                                Day(s): {sec.ssrmeetMtypCode?.trim().toUpperCase() === 'HA'
                                    ? 'TBA'
                                    : getDaysOfWeek(sec) || 'Online/TBA'}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faBookOpenReader} className="mr-1.5 text-gray-500 w-3.5" />
                                Format: {sec.schdDesc || 'N/A'}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faSchool} className="mr-1.5 text-gray-500 w-3.5" />
                                Room: {onlinePop.hasOwnProperty(sec.stvbldgDesc) ? (
                                    <Popover
                                        content={<span className="text-xs">{onlinePop[sec.stvbldgDesc]}</span>}
                                        description={sec.stvbldgDesc || 'Online Info'}
                                        triggerClassName={popoverLinkClass}
                                    >
                                        {sec.stvbldgDesc}
                                    </Popover>
                                ) : (
                                    <>
                                        {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : `- ${sec.roomCode}`}
                                    </>
                                )}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                Duration: {formatDateRangeDuration(sec.ptrmStartDate, sec.ptrmEndDate)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Seats & Waitlist Row */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 space-y-2 md:space-y-0">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
                    {/* Seats */}
                    {parseInt(item.seatsAvail) <= 0 && parseInt(item.maxEnrl) > 0 ? (
                        <div className="inline-flex items-center bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-0.5 rounded-full min-h-8">
                            <FontAwesomeIcon icon={faChair} className="mr-1.5 w-3.5" />
                            Seats: Full
                        </div>
                    ) : (
                        <div className="flex items-center min-h-8">
                            <FontAwesomeIcon icon={faChair} className="mr-1.5 text-gray-500 w-3.5" />
                            <span className={labelClass}>Seats:</span>
                            <span className={valueClass}>{item.enrl || 0}/{item.maxEnrl || 0}</span>
                        </div>
                    )}
                    {/* Progress bar for seats */}
                    {parseInt(item.maxEnrl) > 0 && (
                        <div className="h-2.5 mt-1 bg-gray-200 rounded-full w-32 overflow-hidden">
                            <div
                                className={`h-2.5 rounded-full ${parseInt(item.enrl) / parseInt(item.maxEnrl) >= 0.90
                                    ? 'bg-red-500'
                                    : parseInt(item.enrl) / parseInt(item.maxEnrl) >= 0.8
                                        ? 'bg-yellow-500'
                                        : 'bg-green-500'
                                    }`}
                                style={{ width: `${Math.min((parseInt(item.enrl || 0) / parseInt(item.maxEnrl || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                    )}
                    {/* Waitlist info */}
                    {parseInt(item.waitAvail) > 0 && parseInt(item.waitCapacity) > 0 && (
                        <div className="mt-1.5 inline-flex items-center bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                            Waitlist: {item.waitCount || 0}/{item.waitCapacity || 0}
                        </div>
                    )}
                    {parseInt(item.seatsAvail) <= 0 && parseInt(item.waitCapacity) > 0 && (
                        <div className="h-2.5 mt-1 bg-gray-200 rounded-full w-32 overflow-hidden">
                            <div
                                className="h-2.5 bg-blue-500 rounded-full"
                                style={{ width: `${Math.min((parseInt(item.waitCount || 0) / parseInt(item.waitCapacity || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                    )}
                </div>
                {/* Badges on mobile: show below seats row */}
                <div className="flex flex-wrap items-center gap-1.5 text-xs 2xl:hidden">
                    {(item.attrchrts || '').split(',').map(a => a.trim()).filter(Boolean).map(attr => {
                        let badgeStyle = "font-medium px-2 py-1 rounded-full";
                        if (attr === 'UC') return <span key={attr} className={`bg-purple-100 text-purple-800 ${badgeStyle}`}>Transferable UC</span>;
                        if (attr === 'CSU') return <span key={attr} className={`bg-indigo-100 text-indigo-800 ${badgeStyle}`}>Transferable CSU</span>;
                        if (attr === 'ZTC') return <span key={attr} className={`bg-green-100 text-green-800 ${badgeStyle}`}>ZTC</span>;
                        if (attr === 'QR') return <span key={attr} className={`bg-sky-100 text-sky-800 ${badgeStyle}`}>QR</span>;
                        return null;
                    })}
                </div>
            </div>

            {/* Section footnotes for all meetings, deduplicated */}
            {
                (() => {
                    const uniqueFootnotes = [...new Set(sortedSections
                        .map(sec => sec.sectionFootnotes)
                        .filter(f => f && f.trim()))];
                    return uniqueFootnotes.length > 0 ? (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                            {uniqueFootnotes.map((footnote, i) => (
                                <strong key={i}><span className="text-xs text-gray-600 block" dangerouslySetInnerHTML={createMarkup(footnote)}></span></strong>
                            ))}
                        </div>
                    ) : null;
                })()
            }
        </div >
    );
}

export default CRNColumns;